import { useMemo, useState } from 'react';

import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { decimalPlaces } from '@/utils/format';

import { Listbox } from './listbox';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * The only way money is entered anywhere in Trinity.
 *
 * The user types major units — 25.50 — and the value that leaves this component is
 * `2550`, an integer, alongside its currency. Nothing downstream ever sees a float,
 * because a float is how a treasurer's totals end up a pesewa short and nobody can
 * say why.
 *
 * Two behaviours are load-bearing:
 * - **The currency decides the precision.** 25.505 is refused for GHS and accepted
 *   for a three-decimal currency. The input cannot be more permissive than the
 *   ledger.
 * - **Grouping appears on blur, not while typing.** Separators inserted mid-keystroke
 *   fight the caret. While focused the field is plain digits; on blur it reads the way
 *   the locale writes numbers.
 *
 * Reference: documentation/architecture/widget-architecture.md §3.1.
 */

export interface MoneyInputProps {
  /** Integer minor units, or null for an empty field. */
  value: number | null;
  onChange: (minorUnits: number | null) => void;
  /** Defaults to the church's base currency. */
  currency?: string;
  onCurrencyChange?: (currency: string) => void;
  /**
   * Supplying this shows an inline currency picker. Omit it unless the requirement
   * genuinely needs a per-record currency — most do not.
   */
  allowedCurrencies?: string[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Rejects negatives outright, for fields where a negative is meaningless. */
  min?: number;
  max?: number;
  allowNegative?: boolean;
  id?: string;
  size?: 'sm' | 'md' | 'lg' | 'kiosk';
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const SIZES = {
  sm: 'h-8 text-body-sm',
  md: 'h-10 text-body',
  lg: 'h-12 text-card-title',
  kiosk: 'h-20 text-kiosk-title',
} as const;

export const MoneyInput = ({
  value,
  onChange,
  currency,
  onCurrencyChange,
  allowedCurrencies,
  placeholder,
  disabled = false,
  invalid = false,
  min,
  max,
  allowNegative = false,
  id,
  size = 'md',
  popoverPortal = 'body',
  className,
  ...aria
}: MoneyInputProps) => {
  const baseCurrency = usePreferences((state) => state.baseCurrency);
  const locale = usePreferences((state) => state.locale);
  const activeCurrency = (currency ?? baseCurrency).toUpperCase();
  const places = decimalPlaces(activeCurrency);

  const symbols = useMemo(() => localeSymbols(locale), [locale]);
  const [focused, setFocused] = useState(false);
  // Only meaningful while the field has focus. Unfocused, the display is derived from
  // the value below — rewriting text under a caret is how an input eats digits.
  const [text, setText] = useState(() => plainText(value, places));

  const commit = (next: string) => {
    const normalised = normalise(next, symbols);

    if (normalised === '' || normalised === '-') {
      setText(normalised);
      onChange(null);
      return;
    }

    const minor = toMinor(normalised, places);
    if (minor === null) {
      setText(plainText(value, places));
      return;
    }
    if (!allowNegative && minor < 0) {
      setText(plainText(value, places));
      return;
    }

    if (max !== undefined && minor > max) {
      setText(plainText(max, places));
      onChange(max);
      return;
    }

    if (min !== undefined && minor < min) {
      setText(normalised);
      onChange(null);
      return;
    }

    setText(normalised);
    onChange(minor);
  };

  const handleBlur = () => {
    setFocused(false);
    if (value !== null) {
      setText(plainText(value, places));
      return;
    }

    if (text === '' || text === '-') return;

    const minor = toMinor(normalise(text, symbols), places);
    if (minor === null) {
      setText('');
      return;
    }

    if (max !== undefined && minor > max) {
      setText(plainText(max, places));
      onChange(max);
      return;
    }

    if (min !== undefined && minor < min) {
      setText(plainText(min, places));
      onChange(min);
    }
  };

  const display = focused
    ? text
    : value === null
      ? ''
      : new Intl.NumberFormat(locale, {
          minimumFractionDigits: places,
          maximumFractionDigits: places,
        }).format(value / 10 ** places);

  return (
    <div
      className={cn(
        'flex w-full items-stretch overflow-hidden rounded-md border bg-surface transition-colors',
        'focus-within:border-brand',
        invalid ? 'border-danger' : 'border-border hover:border-border-strong',
        disabled && 'bg-surface-sunken',
        SIZES[size],
        className,
      )}
    >
      {allowedCurrencies && allowedCurrencies.length > 1 ? (
        <div className="w-28 shrink-0 border-r border-border">
          <Listbox
            value={activeCurrency}
            onChange={(next) => next && onCurrencyChange?.(next)}
            options={allowedCurrencies.map((code) => ({ value: code, label: code }))}
            disabled={disabled || !onCurrencyChange}
            popoverPortal={popoverPortal}
            size={size === 'kiosk' ? 'lg' : size}
            className="h-full rounded-none border-0"
          />
        </div>
      ) : (
        <span
          aria-hidden
          className="flex shrink-0 items-center border-r border-border bg-surface-sunken px-3 text-body-sm font-medium text-fg-secondary"
        >
          {activeCurrency}
        </span>
      )}

      <input
        id={id}
        // `inputMode` rather than `type="number"`: a number input brings spinners,
        // scroll-wheel edits and a browser-dependent idea of what a decimal is.
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        value={display}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        onFocus={() => {
          setFocused(true);
          setText(plainText(value, places));
        }}
        onBlur={handleBlur}
        onChange={(event) => commit(event.target.value)}
        className={cn(
          'min-w-0 flex-1 bg-transparent px-3 text-right font-numeric tabular-nums text-fg outline-none',
          'placeholder:text-fg-tertiary disabled:cursor-not-allowed disabled:text-fg-disabled',
        )}
        {...aria}
      />
    </div>
  );
};

const localeSymbols = (locale: string): { group: string; decimal: string } => {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  return {
    group: parts.find((part) => part.type === 'group')?.value ?? ',',
    decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
  };
};

/** What the user sees while typing: digits and a single dot, no grouping. */
const plainText = (minorUnits: number | null, places: number): string => {
  if (minorUnits === null) return '';
  const negative = minorUnits < 0;
  const digits = Math.abs(minorUnits).toString().padStart(places + 1, '0');
  const whole = digits.slice(0, digits.length - places);
  const fraction = places === 0 ? '' : `.${digits.slice(digits.length - places)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
};

/**
 * Anything pasted or typed, reduced to `-123.45`.
 *
 * Handles a paste from a spreadsheet in a locale that groups with dots, which is why
 * the locale's own separators are consulted rather than assumed.
 */
const normalise = (input: string, symbols: { group: string; decimal: string }): string => {
  let text = input.split(symbols.group).join('');
  if (symbols.decimal !== '.') text = text.split(symbols.decimal).join('.');
  text = text.replace(/[^\d.-]/g, '');

  const negative = text.startsWith('-');
  text = text.replace(/-/g, '');

  const [whole = '', ...rest] = text.split('.');
  const fraction = rest.join('');
  const joined = rest.length > 0 ? `${whole}.${fraction}` : whole;

  return `${negative ? '-' : ''}${joined}`;
};

const toMinor = (normalised: string, places: number): number | null => {
  const match = /^(-)?(\d*)(?:\.(\d*))?$/.exec(normalised);
  if (!match) return null;

  const [, sign, whole = '', fraction = ''] = match;
  // Refusing rather than rounding: a church typing 10.005 into a two-decimal
  // currency should be told, not silently given 10.01.
  if (fraction.length > places) return null;
  if (whole === '' && fraction === '') return null;

  const minor = Number(`${whole || '0'}${fraction.padEnd(places, '0')}`);
  return Number.isSafeInteger(minor) ? (sign ? -minor : minor) : null;
};
